import { useState, useEffect, useRef, useCallback } from 'react';
import {
  openChannel, MSG, ROLE,
  DISPLAY_PATH, DISPLAY_WINDOW_NAME, STAGE_PATH, STAGE_WINDOW_NAME,
} from './displayChannel';

/**
 * Control-page side of the outputs:
 *  - the projector (/display), opened on a second screen when the browser allows it;
 *  - the stage/confidence screen (/stage) for the preacher and band.
 * It broadcasts the current state on every change and answers a freshly
 * loaded screen's "hello" so a reload or reopen repopulates immediately.
 * `stage` carries extra info only the stage screen uses (next item, position).
 */
export default function useDisplayOutput({ isLive, content, currentLine, slides, style, backgroundImage, stage }) {
  const [connected, setConnected] = useState(false);
  const [stageConnected, setStageConnected] = useState(false);
  const channelRef = useRef(null);
  const windowRef = useRef(null);
  const stageWindowRef = useRef(null);
  const stateRef = useRef(null);
  const backgroundRef = useRef(null);

  const state = { isLive, content, currentLine, slides, style, stage: stage || null };
  stateRef.current = state;
  const imageId = style.background && style.background.imageId;
  backgroundRef.current = imageId && backgroundImage ? { imageId, blob: backgroundImage } : null;

  useEffect(() => {
    const channel = openChannel();
    channelRef.current = channel;
    if (!channel) return undefined;
    // A message with no role comes from the projector (older display builds).
    const setFor = (role, value) => (role === ROLE.STAGE ? setStageConnected(value) : setConnected(value));
    channel.onmessage = (e) => {
      const type = e.data && e.data.type;
      const role = e.data && e.data.role;
      if (type === MSG.HELLO) {
        setFor(role, true);
        channel.postMessage({ type: MSG.STATE, state: stateRef.current });
        if (backgroundRef.current) channel.postMessage({ type: MSG.BACKGROUND, ...backgroundRef.current });
      } else if (type === MSG.BYE) {
        setFor(role, false);
      }
    };
    // pagehide can be missed (crash, forced close) — double-check the popups.
    const timer = setInterval(() => {
      if (windowRef.current && windowRef.current.closed) {
        windowRef.current = null;
        setConnected(false);
      }
      if (stageWindowRef.current && stageWindowRef.current.closed) {
        stageWindowRef.current = null;
        setStageConnected(false);
      }
    }, 2000);
    return () => {
      clearInterval(timer);
      channel.close();
      channelRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (channelRef.current) channelRef.current.postMessage({ type: MSG.STATE, state });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLive, content, currentLine, JSON.stringify(slides), JSON.stringify(style), JSON.stringify(stage)]);

  // The image Blob travels separately from state so slide changes never
  // re-send it; it is re-sent when the selection changes or a display says hello.
  useEffect(() => {
    if (channelRef.current && backgroundRef.current) {
      channelRef.current.postMessage({ type: MSG.BACKGROUND, ...backgroundRef.current });
    }
  }, [imageId, backgroundImage]);

  /** Opens (or re-focuses) the projector display. Resolves true if a window opened. */
  const open = useCallback(async () => {
    if (windowRef.current && !windowRef.current.closed) {
      windowRef.current.focus();
      return true;
    }
    let features = 'width=1280,height=720';
    try {
      if (window.getScreenDetails) {
        const details = await window.getScreenDetails();
        const other = details.screens.find(sc => sc !== details.currentScreen);
        if (other) {
          features = `left=${other.availLeft},top=${other.availTop},width=${other.availWidth},height=${other.availHeight}`;
        }
      }
    } catch {
      // permission denied or unsupported — use the plain popup
    }
    const win = window.open(DISPLAY_PATH, DISPLAY_WINDOW_NAME, features);
    windowRef.current = win || null;
    return !!win;
  }, []);

  /** Opens (or re-focuses) the stage screen as a plain popup to drag where needed. */
  const openStage = useCallback(async () => {
    if (stageWindowRef.current && !stageWindowRef.current.closed) {
      stageWindowRef.current.focus();
      return true;
    }
    const win = window.open(STAGE_PATH, STAGE_WINDOW_NAME, 'width=1024,height=576');
    stageWindowRef.current = win || null;
    return !!win;
  }, []);

  return { open, connected, openStage, stageConnected };
}
